import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { LazyGrid } from 'components/common'
import ContactSearchWorksApi from 'api/DataEntry/ContactSearchSelect/ContactWorksApi'
import { selectMembershipApiHost } from 'globalSelectors'
import {
    setContactSearchWorksFetchStatusAction,
    setContactSearchWorksFetchResultsAction,
    setContactSearchWorksCurrentPageAction,
    setContactSearchWorksTotalPagesAction,
    setContactSearchWorksFirstAction,
    setContactSearchWorksRowsAction,
    setContactSearchWorksFilterAction,
    setContactSearchWorksSortAction,
    initContactSearchWorksAction
} from '../../store/actions'
import {
    selectContactSearchWorksFetchResults,
    selectContactSearchWorksCurrentPage,
    selectContactSearchWorksTotalPages,
    selectContactSearchWorksRows,
    selectContactSearchWorksFirst,
    selectContactSearchWorksFetchStatus,
    selectContactSearchWorksRequest
} from '../../store/selectors'
import { Badge } from 'primereact/badge'

/**
 * Column definitions for the lazy-loaded works grid
 */
const workTypeTemplate = (rowData) => (
    <Badge
        value={rowData.domain}
        className="custom-badge-base custom-badge-aqua-2"
    />
)

const columnDefinitions = [
{
    columnType: 'worktype',
    title: 'Type',
    mapsToField: 'domain',
    filter: true,
    sortable: true,
    style: { width: '80px' },
    template: workTypeTemplate
},
    {
        columnType: 'text',
        title: 'Title',
        mapsToField: 'workTitle',
        filter: true,
        sortable: true,
        style: { width: '250px' },
        template: (rowData) => rowData.workTitle || '-'
    },
    {
        columnType: 'text',
        title: 'Episode Title',
        mapsToField: 'episodeTitle',
        filter: true,
        sortable: true,
        style: { width: '200px' },
        template: (rowData) => rowData.episodeTitle || '-'
    },
    {
        columnType: 'workcontributiontype',
        title: 'Contribution',
        mapsToField: 'contributionType',
        filter: true,
        sortable: true,
        style: { width: '120px' },
        template: (rowData) => rowData.contributionType || '-'
    },
    {
        columnType: 'text',
        title: 'ISBN',
        mapsToField: 'isbn',
        filter: false,
        sortable: false,
        style: { width: '100px' },
        template: (rowData) => rowData.isbn || '-'
    },
    {
        columnType: 'text',
        title: 'ISSN',
        mapsToField: 'issn',
        filter: false,
        sortable: false,
        style: { width: '100px' },
        template: (rowData) => rowData.issn || '-'
    }
]

/**
 * LazyGrid component for displaying works when a contact has >50 works
 * Uses server-side pagination
 * @param {Object} props
 * @param {string} props.contactId - The contact GUID
 * @param {number} props.worksCount - Total number of works
 */
const WorksLazyGrid = ({ contactId, worksCount }) => {
    const dispatch = useDispatch()
    const apiBaseUrl = useSelector(selectMembershipApiHost)

    // Initialize state for this contact if it doesn't exist
    useEffect(() => {
        dispatch(initContactSearchWorksAction(contactId))
    }, [contactId, dispatch])

    // Redux selectors - pass contactId to get this contact's specific data
    const worksResults = useSelector(selectContactSearchWorksFetchResults(contactId))
    const currentPage = useSelector(selectContactSearchWorksCurrentPage(contactId))
    const totalPages = useSelector(selectContactSearchWorksTotalPages(contactId))
    const rows = useSelector(selectContactSearchWorksRows(contactId))
    const first = useSelector(selectContactSearchWorksFirst(contactId))
    const fetchStatus = useSelector(selectContactSearchWorksFetchStatus(contactId))

    // Create memoized selectors for this contact
    const selectorsForContact = useMemo(() => ({
        selectTotalPages: selectContactSearchWorksTotalPages(contactId),
        selectFirst: selectContactSearchWorksFirst(contactId),
        selectRows: selectContactSearchWorksRows(contactId),
        selectFetchStatus: selectContactSearchWorksFetchStatus(contactId)
    }), [contactId])

    // Create a memoized selector for this contact
    const requestSelectorForContact = useMemo(
        () => (state) => selectContactSearchWorksRequest(contactId)(state),
        [contactId]
    )

    // Create memoized action creators bound to this contactId
    const boundActions = useMemo(() => ({
        setTotalPages: (pages) => setContactSearchWorksTotalPagesAction(contactId, pages),
        setCurrentPage: (page) => setContactSearchWorksCurrentPageAction(contactId, page),
        setFirst: (first) => setContactSearchWorksFirstAction(contactId, first),
        setRows: (rows) => setContactSearchWorksRowsAction(contactId, rows),
        setFilter: (filter) => setContactSearchWorksFilterAction(contactId, filter),
        setSort: (sort) => setContactSearchWorksSortAction(contactId, sort)
    }), [contactId])

    // Prepare data with unique IDs
    const worksResultList = (worksResults?.results || []).map((work, index) => ({
        ...work,
        uniqueId: `${contactId}_${work.workCode || ''}_${work.contributionType || ''}_${index}`
    }))
    const worksTotal = worksResults?.total || worksCount || 0

    return (
        <div className="works-lazy-grid">
            <h5 className="contact-search-panel-title mb-2">
                Works{worksTotal > 0 ? ` (${worksTotal})` : ''}
            </h5>
            <ContactSearchWorksApi
                contactId={contactId}
                endpoint={apiBaseUrl}
                setStatusAction={setContactSearchWorksFetchStatusAction}
                setResultsAction={setContactSearchWorksFetchResultsAction}
                requestSelector={requestSelectorForContact}
            />
            <LazyGrid
                id={`contact_search_${contactId}_works_grid`}
                dataKey="uniqueId"
                data={worksResultList}
                totalCount={worksTotal}
                emptyMessage="No works found"
                columnDefinitions={columnDefinitions}
                selectTotalPages={selectorsForContact.selectTotalPages}
                selectFirst={selectorsForContact.selectFirst}
                selectRows={selectorsForContact.selectRows}
                fetchStatusSelector={selectorsForContact.selectFetchStatus}
                setTotalPagesAction={boundActions.setTotalPages}
                setCurrentPageAction={boundActions.setCurrentPage}
                setFirstAction={boundActions.setFirst}
                setRowsAction={boundActions.setRows}
                setFilterAction={boundActions.setFilter}
                setSortAction={boundActions.setSort}
                currentPage={currentPage}
                scrollHeight="auto"
            />
        </div>
    )
}

export default WorksLazyGrid
